import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy, inject } from "@angular/core";
import { Token } from "@app/models/token.interface";
import { AuthenticationService } from "@app/services/authentication.service";
import { LoggingService } from "@app/services/logging.service";
import { BruudToastrService } from "@app/services/message.service";
import { AppSettings } from "app/app.settings";
import { from, Subject, throwError } from "rxjs";
import { takeUntil, catchError, switchMap, map, mergeMap } from "rxjs/operators";
import { OktaOidcService } from "./okta-oidc.service";
import { Router } from "@angular/router";
import { ProfileService } from "@app/services/profile.service";

@Injectable({
   providedIn: "root"
})
export class OktaService implements OnDestroy {
   private router = inject(Router);
   private oktaOidcService = inject(OktaOidcService);
   private authService = inject(AuthenticationService);
   private log = inject(LoggingService);
   private messageService = inject(BruudToastrService);
   private httpClient = inject(HttpClient);
   private appSettings = inject(AppSettings);
   private profileService = inject(ProfileService);

   private destroyed: Subject<void> = new Subject<void>();

   public ngOnDestroy(): void {
      this.destroyed.next();
      this.destroyed.complete();
   }

   public onOktaLoginCompleted(): void {
      this.login();
   }

   private async login(): Promise<void | boolean> {
      this.log.info("Logging in with Okta");

      let impersonationQuery = "";

      const groups = (await this.oktaOidcService.getGroups()).map(g =>
         g.toUpperCase()
      );
      if (
         groups.includes("MYCLOUDIMPERSONATOR") &&
         !groups.includes("MYCLOUDCUSTOMER") &&
         !groups.includes("MYCLOUDALIAS")
      ) {
         const impersonationId = await this.oktaOidcService.getState(
            "impersonationId"
         );
         if (!impersonationId) {
            return this.router.navigate(["/ng/impersonation"]);
         }

         impersonationQuery = `?impersonationId=${impersonationId}`;
      }

      let ssoToken = await this.oktaOidcService.getAccessToken();
      const headers = new HttpHeaders({
         Authorization: `bearer ${ssoToken}`,
         "Content-Type": "application/json"
      });

      this.httpClient.get<Token>(`${this.appSettings.baseCoreApiUrl}/okta/token${impersonationQuery}`, { headers: headers }).pipe(
         takeUntil(this.destroyed),
         catchError(err => {
            if (err instanceof HttpErrorResponse) {
               if (err.status == 401) {
                  this.messageService.showTranslatedError('MC_NO_ACCESS', 'Error');
               }
               if (err.status == 404) {
                  this.messageService.showTranslatedError('MC_OKTA_USERNOTFOUND', 'Error');
               }
               return throwError(err);
            }
         }),
         switchMap(token => this.authService.processToken(token)),
         map(profile => this.authService.processProfile(profile)),
         switchMap(_ => from(this.oktaOidcService.getState('redirectUrl'))),
      ).subscribe(redirectUrl => {
         const url = decodeURIComponent(redirectUrl ?? '');
         this.log.info(`Okta login complete, redirecting to "${url}"`);

            if (redirectUrl) {
               this.oktaOidcService.setState("redirectUrl", ""); //http://localhost:4200/ng/products/domain-name;domainName=yeet.be
            }

            this.router.navigateByUrl(url);
         });
   }

   public logout(): void {
      this.profileService
         .revokeToken()
         .pipe(
            takeUntil(this.destroyed),
            mergeMap(_ => this.oktaOidcService.isLoggedInObservable()),
            switchMap(loggedIn => {
               if (loggedIn) {
                  return this.oktaOidcService.logoutObservable();
               }

               this.authService.logout();
               location.href = "";
            })
         )
         .subscribe();
   }
}
